import React, { useState, useEffect } from 'react';
import './BattleEffects.css';

export interface FloatingText {
  id: string;
  text: string;
  type: 'damage' | 'healing' | 'critical' | 'passive' | 'status';
  x: number;
  y: number;
  duration?: number;
}

export interface PassiveAbilityNotification {
  id: string;
  abilityName: string;
  unitName: string;
  description: string;
  type: 'berserker' | 'vampiric' | 'guardian' | 'critical-master' | 'skill-reflect' | 'regeneration';
  duration?: number;
}

interface BattleEffectsProps {
  floatingTexts: FloatingText[];
  passiveNotifications: PassiveAbilityNotification[];
  onFloatingTextComplete: (id: string) => void;
  onPassiveNotificationComplete: (id: string) => void;
}

const BattleEffects: React.FC<BattleEffectsProps> = ({
  floatingTexts,
  passiveNotifications,
  onFloatingTextComplete,
  onPassiveNotificationComplete
}) => {
  const [activeTexts, setActiveTexts] = useState<FloatingText[]>([]);
  const [activeNotifications, setActiveNotifications] = useState<PassiveAbilityNotification[]>([]);

  useEffect(() => {
    setActiveTexts(floatingTexts);
    
    // Auto-remove floating texts after their duration
    floatingTexts.forEach(text => {
      const duration = text.duration || 2000;
      setTimeout(() => {
        onFloatingTextComplete(text.id);
      }, duration);
    });
  }, [floatingTexts, onFloatingTextComplete]);

  useEffect(() => {
    setActiveNotifications(passiveNotifications);
    
    // Auto-remove notifications after their duration
    passiveNotifications.forEach(notification => {
      const duration = notification.duration || 3000;
      setTimeout(() => {
        onPassiveNotificationComplete(notification.id);
      }, duration);
    });
  }, [passiveNotifications, onPassiveNotificationComplete]);

  const getFloatingTextClass = (type: string) => {
    const baseClass = 'floating-text';
    switch (type) {
      case 'damage':
        return `${baseClass} damage-text`;
      case 'healing':
        return `${baseClass} healing-text`;
      case 'critical':
        return `${baseClass} critical-text`;
      case 'passive':
        return `${baseClass} passive-text`;
      case 'status':
        return `${baseClass} status-text`;
      default:
        return baseClass;
    }
  };

  const getPassiveIcon = (type: string) => {
    switch (type) {
      case 'berserker':
        return '⚡';
      case 'vampiric':
        return '🩸';
      case 'guardian':
        return '🛡️';
      case 'critical-master':
        return '✨';
      case 'skill-reflect':
        return '🔮';
      case 'regeneration':
        return '💚';
      default:
        return '🔥';
    }
  };

  return (
    <div className="battle-effects-container">
      {/* Floating Combat Text */}
      {activeTexts.map(text => (
        <div
          key={text.id}
          className={getFloatingTextClass(text.type)}
          style={{
            left: `${text.x}px`,
            top: `${text.y}px`,
          }}
        >
          {text.text}
        </div>
      ))}

      {/* Passive Ability Notifications */}
      <div className="passive-notifications">
        {activeNotifications.map(notification => (
          <div
            key={notification.id}
            className={`passive-notification passive-${notification.type}`}
          >
            <div className="passive-icon">
              {getPassiveIcon(notification.type)}
            </div>
            <div className="passive-content">
              <div className="passive-title">
                {notification.abilityName}
              </div>
              <div className="passive-description">
                {notification.unitName}: {notification.description}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BattleEffects;